import React from 'react';
import './EthicsPage.css';

export function EthicsPage() {
  return (
    <div className="ethics-page">
      <div className="container">
        <header className="page-header">
          <h1>AI Ethics & Responsible Use</h1>
          <p>Guidelines for using Supply Chain Brutus (or any generative AI) ethically and effectively in your academic journey</p>
          <p className="last-reviewed">Last reviewed: August 2026</p>
        </header>

        {/* 1. Academic Integrity */}
        <section className="ethics-section">
          <h2>1. Academic Integrity</h2>
          <div className="components-grid">
            <div className="component-card do-card">
              <h3>✅ DO</h3>
              <ul>
                <li>Use AI as a learning aid—not a shortcut</li>
                <li>Cite AI assistance when using</li>
                <li>Always verify AI-generated information against your course materials</li>
                <li>Practice and test your understanding with AI-generated exercises</li>
              </ul>
            </div>
            <div className="component-card dont-card">
              <h3>❌ DON'T</h3>
              <ul>
                <li>Submit AI-generated work as your own — This is <strong>plagiarism</strong></li>
                <li>Use AI to complete assignments when prohibited by your instructor — This is <strong>academic misconduct</strong></li>
                <li>Share exam questions or restricted materials with the AI — This may <strong>violate intellectual property laws</strong></li>
                <li>Bypass learning objectives set by your course — This <strong>undercuts your own education</strong></li>
              </ul>
            </div>
          </div>
          <br />
          <p><strong>The Bottom Line: </strong>AI is a powerful tool, but your critical thinking and understanding are what make you valuable.</p>
        </section>

        {/* 2. Core Ethical Principles */}
        <section className="ethics-section">
          <h2>2. Core Ethical Principles</h2>
          <div className="components-grid">
            <div className="component-card">
              <h3>Transparency</h3>
              <p>Be open about when and how you use AI assistance</p>
            </div>
            <div className="component-card">
              <h3>Accountability</h3>
              <p>Humans are held responsible for their actions, not AI</p>
            </div>
            <div className="component-card">
              <h3>Human-in-the-Loop</h3>
              <p>Treat AI as an advisor—<strong>trust but verify</strong></p>
            </div>
          </div>
        </section>

        {/* 3. Practical Risks */}
        <section className="ethics-section">
          <h2>3. Practical Risks</h2>
          <div className="components-grid force-3">
            <div className="component-card">
              <h3>Knowledge Cutoff</h3>
              <p>AI may lack awareness of recent developments due to training data cutoffs</p>
            </div>
            <div className="component-card">
              <h3>Errors & Hallucinations</h3>
              <p>AI can produce inaccurate or fabricated outputs with conviction</p>
            </div>
            <div className="component-card">
              <h3>Lack of Real-World Experience</h3>
              <p>AI has no practical supply chain expertise, let alone with your specific tasks...you wouldn't trust a human with those qualifications, would you?</p>
            </div>
            <div className="component-card">
              <h3>Plagiarism & Intellectual Property</h3>
              <p>AI may unintentionally commit copyright violations by reproducing its training data verbatim</p>
            </div>
            <div className="component-card">
              <h3>Training Data Bias</h3>
              <p>What's in AI training data? We don't know and it may perpetuate inequities</p>
            </div>
          </div>
        </section>

        {/* 4. Environmental & Social Impact */}
        <section className="ethics-section">
          <h2>4. Environmental & Social Impact</h2>
          <div className="components-grid force-2">
            <div className="component-card">
              <h3>Rising Carbon Emissions</h3>
              <p>Whether AI's carbon footprint keeps climbing or starts to fall is still an open question, but reducing it carries economic incentives alongside the environmental ones—energy costs money, so efficiency pays for itself. A 2025 <a href="https://mit-genai.pubpub.org/pub/8ulgrckc/release/2" target="_blank" rel="noopener noreferrer">MIT analysis</a> mapped early efficiency trajectories; the <a href="https://www.iea.org/reports/electricity-2026" target="_blank" rel="noopener noreferrer">IEA's Electricity 2026</a> outlook shows demand still outpacing those gains, but the underlying incentive to close that gap isn't going away.</p>
            </div>
            <div className="component-card">
              <h3>The Efficiency Dividend</h3>
              <p>Larger AI models can burn dozens of times the energy of smaller ones per query, but that gap is exactly why smaller, distilled models and efficient chips are becoming a competitive edge. <a href="https://www.technologyreview.com/2025/05/20/1116327/ai-energy-usage-climate-footprint-big-tech/" target="_blank" rel="noopener noreferrer">MIT Technology Review's</a> analysis tracks how energy use per response is falling as efficiency turns into a cost advantage.</p>
            </div>
            <div className="component-card">
              <h3>Thirsty Data Centers</h3>
              <p>AI's cooling systems can consume millions of gallons of water a day, with demand projected to climb sharply, yet data centers remain under 1% of national water use—meaning the real strain lands on specific water-stressed regions. <a href="https://www.brookings.edu/articles/ai-data-centers-and-water/" target="_blank" rel="noopener noreferrer">Brookings</a> maps where that pressure actually falls.</p>
            </div>
            <div className="component-card">
              <h3>The Nuclear Bet</h3>
              <p>Tech giants are signing nuclear and small-reactor deals to power AI data centers, but new nuclear costs roughly three times as much as solar or wind, and the pledged dollars are a fraction of actual data-center spending. The <a href="https://thebulletin.org/2026/07/data-centers-powered-by-next-gen-nuclear-dont-fall-for-big-techs-pr-hype/" target="_blank" rel="noopener noreferrer">Bulletin of the Atomic Scientists</a> weighs the hype against the math.</p>
            </div>
            <div className="component-card">
              <h3>Who Pays for the Buildout</h3>
              <p>The race to power AI is showing up on utility bills—U.S. electricity prices are up 42% since 2019, with data-center-heavy regions absorbing some of the increase—but the same pressure is accelerating grid investment and tariff reform. <a href="https://www.energypolicy.columbia.edu/publications/the-effects-of-load-growth-on-electricity-prices-in-the-united-states-a-literature-review/" target="_blank" rel="noopener noreferrer">Columbia's Center on Global Energy Policy</a> traces where those costs land.</p>
            </div>
            <div className="component-card">
              <h3>Inequality</h3>
              <p>AI automation may concentrate benefits while displacing workers, yet extreme inequality undermines the consumer base businesses need to prosper. <a href="https://hbr.org/2025/05/how-companies-can-navigate-the-age-of-ai-driven-inequality" target="_blank" rel="noopener noreferrer">Harvard Business Review</a> examines strategies for inclusive AI practices.</p>
            </div>
            <div className="component-card">
              <h3>Supercharged Misinformation</h3>
              <p>AI can rapidly generate false information at scale, but the same technology provides tools to detect and counter misinformation. <a href="https://hdsr.mitpress.mit.edu/pub/vn2v6ety/release/1" target="_blank" rel="noopener noreferrer">Harvard Data Science Review</a> explores this double-edged dynamic.</p>
            </div>
            <div className="component-card">
              <h3>Job Replacement</h3>
              <p>While AI raises job displacement concerns, 2025 <a href="https://www.dallasfed.org/research/economics/2025/0603" target="_blank" rel="noopener noreferrer">Dallas Fed research</a> found little correlation between AI exposure and projected job losses—but a year later, <a href="https://digitaleconomy.stanford.edu/publications/canaries-in-the-coal-mine/" target="_blank" rel="noopener noreferrer">Stanford's Digital Economy Lab</a> traces a narrower, real effect: employment for young workers in AI-exposed roles now running 19% below trend, driven by fewer new hires rather than layoffs. The bigger challenge remains reskilling entry-level talent into a tighter market.</p>
            </div>
            <div className="component-card">
              <h3>Data Privacy and Cybersecurity</h3>
              <p>AI's vast data requirements create privacy risks, yet these same risks drive companies to develop robust protections to avoid penalties and maintain trust. <a href="https://hai.stanford.edu/policy/white-paper-rethinking-privacy-ai-era-policy-provocations-data-centric-world" target="_blank" rel="noopener noreferrer">Stanford HAI</a> explores evolving privacy frameworks.</p>
            </div>
            <div className="component-card">
              <h3>AI Sycophancy</h3>
              <p>Models tuned for engagement may prioritize approval over accuracy, in extreme cases fostering emotional dependence or encouraging risky behavior, but growing public scrutiny is pushing developers toward safeguards that reward honesty over agreeableness. <a href="https://www.law.georgetown.edu/tech-institute/insights/tech-brief-ai-sycophancy-openai-2/" target="_blank" rel="noopener noreferrer">Georgetown Law's Tech Institute</a> examines this dynamic.</p>
            </div>
          </div>
        </section>
        {/* 5. Regulatory Issues */}
        <section className="ethics-section">
          <h2>5. Regulatory Landscape</h2>
          <p>Current AI policy varies by region—oversight gaps can lead to unintended consequences.</p>
          <p>Visit <a href="https://righttowarn.ai" target="_blank" rel="noopener noreferrer">Right to Warn</a> to learn more about advocating for clear AI safety standards and accountability.</p>
        </section>
      </div>
    </div>
  );
}
